import type { Deal } from "@/lib/types/database/deals";

export interface HealthCompany {
  id: string;
  name: string;
  email?: string;
  schedule_status?: string | null;
  budget_status?: string | null;
  scope_status?: string | null;
  alignment_status?: string | null;
}

export interface HealthDimension {
  name: string;
  status: string | null | undefined;
}

export const HEALTH_DIMENSION_LABELS = [
  { key: "schedule_status", name: "Schedule" },
  { key: "budget_status", name: "Budget/Margin" },
  { key: "scope_status", name: "Scope Control" },
  { key: "alignment_status", name: "Client Alignment" },
] as const;

type HealthStatusKey = (typeof HEALTH_DIMENSION_LABELS)[number]["key"];

export function getCompanyHealthStatuses(
  company: HealthCompany
): Array<string | null | undefined> {
  return HEALTH_DIMENSION_LABELS.map(
    (d) => company[d.key as HealthStatusKey]
  );
}

export type CompanyHealthBucket = "red" | "yellow" | "green";

/** Minimal shape the health classifier needs (matches both `HealthCompany` and the GraphQL node). */
export interface CompanyHealthStatusInput {
  schedule_status?: string | null;
  budget_status?: string | null;
  scope_status?: string | null;
  alignment_status?: string | null;
}

/**
 * SINGLE SOURCE OF TRUTH for project-health classification.
 *
 * A company is bucketed by its WORST dimension, considering ONLY rated projects:
 *   - any dimension red   → "red"
 *   - else any yellow     → "yellow"
 *   - else (all green)    → "green"
 *   - NO dimension set    → null ("not rated" — excluded from Active Projects + the color bars)
 *
 * Both the metric-card FACE counts (`calculateHealthMetrics`) and the DRILLDOWN
 * filter (`HealthDrilldownModal`) call this, so a color's headline count equals
 * the number of companies in its drilldown — they cannot diverge.
 */
export function classifyCompanyHealth(
  company: CompanyHealthStatusInput
): CompanyHealthBucket | null {
  const statuses = [
    company.schedule_status,
    company.budget_status,
    company.scope_status,
    company.alignment_status,
  ].filter((s): s is string => s !== null && s !== undefined);

  if (statuses.length === 0) return null;
  if (statuses.includes("red")) return "red";
  if (statuses.includes("yellow")) return "yellow";
  return "green";
}

export function getHealthDimensions(company: HealthCompany): HealthDimension[] {
  return HEALTH_DIMENSION_LABELS.map((d) => ({
    name: d.name,
    status: company[d.key as HealthStatusKey],
  })).filter((d) => d.status);
}

/**
 * Minimal Deal-shaped stub for CEO dashboard action handlers.
 * The handlers only read `id` (logActivityOnDeal) or `id` + `title` + `revenue`
 * (sendEmailToDeal), but their signatures require `Deal`. Safe defaults satisfy
 * the required fields without losing type checking.
 */
export function companyToDealStub(company: HealthCompany): Deal {
  return {
    id: company.id,
    company_id: company.id,
    title: company.name,
    revenue: 0,
    currency: "USD",
    stage: "lead",
    close_probability: 0,
    is_recurring: false,
    is_committed: false,
    last_activity_at: "",
    created_at: "",
    updated_at: "",
  };
}
